import { useState, useRef, useCallback, useEffect } from "react";
import { auth } from "../firebase.js";

const API_BASE = "https://bluebirddocumentationadmin.pythonanywhere.com";

export function useUserMentionTextarea(context) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [mentionedUids, setMentionedUids] = useState([]);
  const triggerIndexRef = useRef(null);
  const activeSetValueRef = useRef(null);
  const activeTextareaRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const contextRef = useRef(context);
  useEffect(() => { contextRef.current = context; }, [context]);

  const searchUsers = useCallback(async (searchQuery) => {
    const ctx = contextRef.current;
    setLoading(true);
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      if (!token) {
        setResults([]);
        return;
      }

      let url;
      let headers = { Authorization: token };

      if (ctx?.mode === "workspace" && ctx.workspaceId) {
        url = `${API_BASE}/workspaces/${ctx.workspaceId}/mention-users/?q=${encodeURIComponent(searchQuery)}`;
        headers = {
          Authorization: token,
          "auth-Token": `Bearer ${token}`,
          "Requesting-User-Uid": user.uid,
        };
      } else if (ctx?.mode === "documentation" && ctx.docType && ctx.docName) {
        url = `${API_BASE}/documentation/mention-users/?type=${encodeURIComponent(ctx.docType)}&name=${encodeURIComponent(ctx.docName)}&q=${encodeURIComponent(searchQuery)}`;
      } else {
        setResults([]);
        return;
      }

      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setResults(data);
        setHighlightIndex(0);
      }
    } catch (err) {
      console.error("[UserMentionTextarea] Search error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setResults([]);
    triggerIndexRef.current = null;
    activeSetValueRef.current = null;
    activeTextareaRef.current = null;
  }, []);

  const selectUser = useCallback((user) => {
    const textarea = activeTextareaRef.current;
    const setValue = activeSetValueRef.current;
    if (!textarea || !setValue || triggerIndexRef.current === null) return;

    const cursorPos = textarea.selectionStart;
    const atIndex = triggerIndexRef.current;
    const displayName = user.name || user.email;
    const mention = `@${displayName} `;
    const currentValue = textarea.value;

    const newValue = currentValue.slice(0, atIndex) + mention + currentValue.slice(cursorPos);
    setValue(newValue);

    if (user.uid) {
      setMentionedUids((prev) => prev.includes(user.uid) ? prev : [...prev, user.uid]);
    }

    const newCursorPos = atIndex + mention.length;
    setTimeout(() => {
      if (textarea) {
        textarea.selectionStart = newCursorPos;
        textarea.selectionEnd = newCursorPos;
        textarea.focus();
      }
    }, 0);

    setIsOpen(false);
    setQuery("");
    setResults([]);
    triggerIndexRef.current = null;
    activeSetValueRef.current = null;
    activeTextareaRef.current = null;
  }, []);

  const handleTextareaChange = useCallback((e, setValue) => {
    const textarea = e.target;
    const cursorPos = textarea.selectionStart;
    const value = textarea.value;
    const textBeforeCursor = value.slice(0, cursorPos);

    // Find the most recent @ before cursor
    let atIndex = -1;
    for (let i = cursorPos - 1; i >= 0; i--) {
      if (textBeforeCursor[i] === "@") {
        const charBefore = i > 0 ? textBeforeCursor[i - 1] : null;
        if (charBefore === null || charBefore === " " || charBefore === "\n" || charBefore === "\t") {
          atIndex = i;
        }
        break;
      }
      if (textBeforeCursor[i] === " " || textBeforeCursor[i] === "\n") {
        break;
      }
    }

    if (atIndex === -1) {
      if (isOpen) close();
      return;
    }

    const textAfterAt = textBeforeCursor.slice(atIndex + 1);
    if (textAfterAt.includes(" ") || textAfterAt.includes("\n")) {
      if (isOpen) close();
      return;
    }

    triggerIndexRef.current = atIndex;
    activeSetValueRef.current = setValue;
    activeTextareaRef.current = textarea;

    const rect = textarea.getBoundingClientRect();
    setPosition({ top: rect.bottom + 4, left: rect.left });
    setIsOpen(true);
    setQuery(textAfterAt);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      searchUsers(textAfterAt.trim());
    }, 250);
  }, [isOpen, close, searchUsers]);

  const handleTextareaKeyDown = useCallback((e) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && results.length > 0) {
      e.preventDefault();
      selectUser(results[highlightIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  }, [isOpen, results, highlightIndex, selectUser, close]);

  const clearMentionedUids = useCallback(() => setMentionedUids([]), []);

  return {
    dropdownProps: {
      isOpen,
      query,
      results,
      loading,
      position,
      highlightIndex,
      onSelect: selectUser,
      onClose: close,
    },
    handleTextareaChange,
    handleTextareaKeyDown,
    mentionedUids,
    clearMentionedUids,
  };
}
